import "core-js/modules/es6.object.define-property";
import "core-js/modules/es6.object.assign";
import "core-js/modules/es6.symbol";
import "core-js/modules/es6.array.index-of";
import "core-js/modules/web.dom.iterable";
import "core-js/modules/es6.array.iterator";
import "core-js/modules/es6.object.to-string";
import "core-js/modules/es6.object.keys";
import _isUndefined from "lodash/isUndefined";

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { Fragment, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useId } from '@nerdwallet/react-accessibility';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import { useDialogState, Dialog, DialogBackdrop, DialogDisclosure } from 'reakit/Dialog';
import { Transition } from 'react-transition-group';
import style from './modal-impl.module.less'; // used internally in the bridge between Modal/ModalContent and ModalImpl

var DialogStateContext = React.createContext(null); // export internal DialogStateContext for usage in `Modal`'s use of ModalContent

export { DialogStateContext }; // thanks @reuben

var usePrevious = function usePrevious(value) {
  var ref = useRef(null);
  useEffect(function () {
    ref.current = value;
  });
  return ref.current;
};

var ModalImpl = function ModalImpl(_ref) {
  var animateMS = _ref.animateMS,
      ariaLabel = _ref['aria-label'],
      ariaLabelledby = _ref['aria-labelledby'],
      bodyOpenClassName = _ref.bodyOpenClassName,
      children = _ref.children,
      className = _ref.className,
      hasScrollLock = _ref.hasScrollLock,
      id = _ref.id,
      isCentered = _ref.isCentered,
      isOpen = _ref.isOpen,
      isScrollable = _ref.isScrollable,
      onRequestClose = _ref.onRequestClose,
      onClose = _ref.onClose,
      onOpen = _ref.onOpen,
      overlayClassName = _ref.overlayClassName,
      shouldCloseOnOverlayClick = _ref.shouldCloseOnOverlayClick,
      triggerProp = _ref.trigger,
      onKeyDown = _ref.onKeyDown,
      htmlProps = _objectWithoutProperties(_ref, ["animateMS", "aria-label", "aria-labelledby", "bodyOpenClassName", "children", "className", "hasScrollLock", "id", "isCentered", "isOpen", "isScrollable", "onRequestClose", "onClose", "onOpen", "overlayClassName", "shouldCloseOnOverlayClick", "trigger", "onKeyDown"]);

  var dialogState = useDialogState({
    modal: true,
    baseId: useId(id),
    animated: animateMS,
    // when `onRequestClose` is supplied, the modal can only be
    // closed by externally managing the `isOpen` prop
    visible: isOpen
  });
  var visible = dialogState.visible,
      setVisible = dialogState.setVisible; // update visibility from external control

  var prevIsOpen = usePrevious(isOpen);
  useEffect(function () {
    // first render?
    if (prevIsOpen === null) return; // externally controlled open state has changed?

    if (prevIsOpen === isOpen) return; // update the internal dialog visibility state

    setVisible(isOpen);
  }, [isOpen, prevIsOpen, setVisible]); // Handlers: hook up onOpen & onClose
  // (previously named: onAfterOpen & onRequestClose)
  // Note: onAfterOpen & onRequestClose prop names were
  // deprecated and renamed because this component's visibility
  // is managed internally but can be controlled externally

  var prevVisible = usePrevious(visible);
  useEffect(function () {
    if (prevVisible === visible) return;
    if (visible && onOpen) onOpen();
    if (visible) return;
    if (prevVisible === null) return;
    if (onClose) onClose();
  }, [visible, prevVisible, onClose, onOpen]); // hook up backwards compatible: [DEPRECATED] bodyOpenClassName

  /* eslint-disable react-hooks/rules-of-hooks */

  if (process.env.NODE_ENV !== 'production') {
    useDeprecatedPropsWarning({
      component: 'ModalImpl',
      deprecatedProps: [{
        deprecated: 'bodyOpenClassName',
        migration: 'This prop was a pass-through to previous implementation on top of react-modal. It should not be used anymore',
        version: '8.10.0'
      }],
      props: {
        bodyOpenClassName: bodyOpenClassName
      }
    });
  }
  /* eslint-enable react-hooks/rules-of-hooks */


  useEffect(function () {
    if (!bodyOpenClassName || !visible) return undefined;
    document.body.classList.add(bodyOpenClassName);
    return function () {
      return document.body.classList.remove(bodyOpenClassName);
    };
  }, [visible, bodyOpenClassName]); // hide overflow on body and html when modal dialog is visible
  // Note: this is to maintain backwards compatibality with pre 8.10.0 implementation

  useEffect(function () {
    if (!visible) return undefined;
    document.body.classList.add(style.overflowHidden);
    document.documentElement.classList.add(style.overflowHidden);
    return function () {
      document.body.classList.remove(style.overflowHidden);
      document.documentElement.classList.remove(style.overflowHidden);
    };
  }, [visible, bodyOpenClassName]); // optional trigger component

  var trigger = triggerProp;

  if (typeof trigger === 'string') {
    trigger = /*#__PURE__*/React.createElement("span", null, trigger);
  } // optional "managed modal" interaction is activated
  // when `onRequestClose` is supplied, the modal can only be
  // closed by externally managing the `isOpen` prop


  var hideOnEsc = !onRequestClose;
  var hideOnClickOutside = true;

  if (onRequestClose) {
    hideOnClickOutside = false;
  } else if (!_isUndefined(shouldCloseOnOverlayClick)) {
    hideOnClickOutside = shouldCloseOnOverlayClick;
  } // props for enabling "managed modal" for DialogBackdrop: custom ref and a click handler


  var dialogBackdropRef = useRef(null);
  var dialogBackgroundInteractiveCloseProps = {
    ref: dialogBackdropRef,
    onClick: useCallback(function (ev) {
      if (dialogBackdropRef.current !== ev.target) return;
      if (onRequestClose) onRequestClose();
    }, [onRequestClose, dialogBackdropRef])
  }; // props for enabling "managed modal" for Dialog: reakit `hideOnEsc` and `hideOnClickOutside` + custom onKeyDown handler

  var dialogInteractiveCloseProps = {
    hideOnEsc: hideOnEsc,
    hideOnClickOutside: hideOnClickOutside,
    onKeyDown: useCallback(function (ev) {
      if (onKeyDown) onKeyDown(ev);
      if (ev.defaultPrevented) return;
      if (ev.key !== 'Escape') return;
      if (onRequestClose) onRequestClose();
    }, [onKeyDown, onRequestClose])
  };
  return /*#__PURE__*/React.createElement(Fragment, null, trigger ? /*#__PURE__*/React.createElement(DialogDisclosure, _extends({}, dialogState, {
    ref: trigger.ref
  }, trigger.props), function (disclosureProps) {
    return /*#__PURE__*/React.cloneElement(trigger, disclosureProps);
  }) : null, /*#__PURE__*/React.createElement(Transition, {
    "in": visible,
    timeout: {
      enter: 0,
      exit: animateMS
    },
    mountOnEnter: true,
    unmountOnExit: true
  }, function (transitionState) {
    var _classNames;

    var ENTERED = transitionState === 'entered';
    return /*#__PURE__*/React.createElement(DialogBackdrop, _extends({
      as: "div"
    }, dialogState, dialogBackgroundInteractiveCloseProps, {
      className: classNames(overlayClassName, style.overlay, (_classNames = {}, _defineProperty(_classNames, style.overlayCentered, isCentered), _defineProperty(_classNames, style.overlayScrollable, isScrollable), _classNames)),
      style: {
        transition: "background-color ".concat(animateMS, "ms linear"),
        backgroundColor: "rgba(0,0,0,".concat(ENTERED ? 0.4 : 0, ")")
      }
    }), /*#__PURE__*/React.createElement(Dialog, _extends({
      as: "div"
    }, dialogState, {
      className: classNames(className, style.modal, style.mobileFillViewport, _defineProperty({}, style.modalCentered, isCentered)) // styles for transitions
      ,
      style: {
        transition: "opacity ".concat(animateMS, "ms ease, transform ").concat(animateMS, "ms ease"),
        opacity: ENTERED ? 1 : 0,
        transform: "translateY(".concat(ENTERED ? 0 : -24, "px)")
      } // interactive close behaviors are triggered when `onRequestClose` prop is provided

    }, dialogInteractiveCloseProps, {
      // end interactive close behaviors
      preventBodyScroll: hasScrollLock // explicit aria props
      ,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledby // spread the rest

    }, htmlProps), /*#__PURE__*/React.createElement(DialogStateContext.Provider, {
      value: dialogState
    }, children)));
  }));
};

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require
  var PropTypes = require('prop-types');

  ModalImpl.propTypes = {
    /**
     * Modal content
     */
    children: PropTypes.node,

    /**
     * duration for modal visual animations
     */
    animateMS: PropTypes.number,

    /**
     * id string applied to modal content dialog
     */
    id: PropTypes.string,

    /**
     * Applies styles to center the modal in the viewport
     */
    isCentered: PropTypes.bool,

    /**
     * Describes if the modal should be shown or not.
     */
    isOpen: PropTypes.bool,

    /**
     * Applies styles to make the modal scrollable when its height extends beyond the viewport
     */
    isScrollable: PropTypes.bool,

    /**
     * Will be run after the modal has opened.
     */
    onOpen: PropTypes.func,

    /**
     * Will be run when the modal is closed (either by clicking on overlay or pressing ESC)
     * Note: It is not called if isOpen is externally changed.
     */
    onClose: PropTypes.func,

    /**
     * Will be run when the modal is requested to be closed (either by clicking on overlay or pressing ESC)
     * Note: It is not called if isOpen is changed by other means.
     */
    onRequestClose: PropTypes.func,

    /**
     * A boolean flag to allow consumers to opt out of the
     * body-scroll-lock which is enabled by default. Useful
     * for instances of full viewport modal on mobile.
     *
     * See https://nerdwallet.atlassian.net/browse/EM-3381
     */
    hasScrollLock: PropTypes.bool,

    /**
     * className to be applied to the overlay (modal background).
     */
    overlayClassName: PropTypes.string,

    /**
     * className to be applied to the modal (content)
     */
    className: PropTypes.string,

    /**
     * Indicates if the modal should close when the overlay is clicked
     * (clicking outside the modal)
     */
    shouldCloseOnOverlayClick: PropTypes.bool,

    /**
     * Element to render that when clicked will open the Modal
     */
    trigger: PropTypes.node,

    /**
     * className to be applied to the document.body (must be a constant string).
     * See the [React Modal docs](http://reactcommunity.org/react-modal/styles/)
     * @deprecated
     */
    bodyOpenClassName: PropTypes.string
  };
}

ModalImpl.defaultProps = {
  animateMS: 300,
  bodyOpenClassName: null,
  children: null,
  className: '',
  hasScrollLock: true,
  isCentered: true,
  isScrollable: true,
  onOpen: null,
  onClose: null,
  overlayClassName: '',
  trigger: null,
  // default behavior is true, but presence of onRequestClose overrides shouldCloseOnOverlayClick
  shouldCloseOnOverlayClick: undefined,
  onRequestClose: undefined
};
export default ModalImpl;